// src/prototype/components/EAUploadPathsPanel.tsx
// ============================================================
// THE TWO PATHS — framing copy plus the three named instrument cards
//
// Sits above the upload area. Path A (drop anything) is unchanged and stays unchanged; this
// panel adds the structured path beside it rather than replacing it.
//
// ─────────────────────────────────────────────────────────────────────────────
// NO MEASURE COUNT IS TYPED IN THIS FILE.
//
// Each card's count is rendered from its instrument's measureIds.length. The copy names the
// areas a template covers and the card renders the number, so a binding change moves the
// number and the sentence stays true. A typed count would be a literal a component holds
// that the data can derive, which the coverage layer does not allow anywhere.
//
// D1'S CARD MAKES NO RE-RANKING CLAIM. Its effect routes largely through the organizational
// risk coefficient, which is org-level and moves score level and band placement across every
// opportunity equally. A reordered list is not something an org-level input can promise, so
// the copy does not promise one. A test asserts the words rank, reorder and re-rank appear
// nowhere in it.
//
// ALL THREE CARDS SERVE A REAL DOWNLOAD. The fallback below stays for an instrument added
// without a template: a download control that resolved to nothing would be worse than no
// control, so it degrades to example-only rather than rendering a dead link.
// ─────────────────────────────────────────────────────────────────────────────
// ============================================================

import { Download, Eye, FileSpreadsheet } from 'lucide-react';
import { INSTRUMENTS, type Instrument } from '../../data/coverage/gapInstruments';
import { TEMPLATE_ASSETS } from '../../data/coverage/templateAssets';

// ---- copy. Counts arrive as parameters; none is held here. ----

const measureCount = (n: number) => `${n} ${n === 1 ? 'measure' : 'measures'}`;

const PATH_A_HEADING = 'Start with what you already have.';
const PATH_A_BODY =
  'Drop in your strategic plan, board materials, or any document describing how your organization works. No preparation needed.';

const PATH_B_HEADING = 'Go deeper with a structured inventory.';
const PATH_B_BODY =
  'Four short templates cover the areas where specifics change your scores the most. They take real effort. They are also the difference between a system that has read about you and one that has measured you.';

const GENERIC_NOTE =
  'Other documents still help. They inform context and narrative. The named templates above are the ones that move scores directly.';

const NO_TEMPLATE_NOTE = 'Template coming soon. Open the completed example to see the shape.';

/** Cards render for instruments that carry card copy. Path A has none by design. */
const CARD_INSTRUMENTS: Instrument[] = INSTRUMENTS.filter((i) => i.card);

export function EAUploadPathsPanel({ className = '' }: { className?: string }) {
  return (
    <div className={`space-y-4 ${className}`}>
      {/* ---- The two paths, stated once ---- */}
      <div className="bg-white rounded-xl border border-[#e5e7eb] p-5 space-y-3">
        <div>
          <p className="text-sm font-semibold text-[#18365E]">{PATH_A_HEADING}</p>
          <p className="text-sm text-[#717182] leading-snug mt-0.5">{PATH_A_BODY}</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-[#18365E]">{PATH_B_HEADING}</p>
          <p className="text-sm text-[#717182] leading-snug mt-0.5">{PATH_B_BODY}</p>
        </div>
      </div>

      {/* ---- The three named templates ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {CARD_INSTRUMENTS.map((instrument) => (
          <InstrumentCardView key={instrument.id} instrument={instrument} />
        ))}
      </div>

      <p className="text-xs text-[#717182] leading-snug">{GENERIC_NOTE}</p>
    </div>
  );
}

function InstrumentCardView({ instrument }: { instrument: Instrument }) {
  const card = instrument.card;
  if (!card) return null;

  const assets = TEMPLATE_ASSETS[instrument.id as keyof typeof TEMPLATE_ASSETS];

  return (
    <div className="bg-white rounded-xl border border-[#e5e7eb] p-4 flex flex-col h-full">
      <div className="flex items-start gap-2 mb-2">
        <FileSpreadsheet className="w-4 h-4 mt-0.5 shrink-0 text-[#FF6E4C]" />
        <div>
          <h3 className="text-sm font-semibold text-[#18365E] leading-snug">{instrument.label}</h3>
          <p className="text-[11px] text-[#717182] mt-0.5">
            {/* Derived. See the header note. */}
            {measureCount(instrument.measureIds.length)}
          </p>
        </div>
      </div>

      <dl className="space-y-2 flex-1">
        <Field term="What it is" detail={card.whatItIs} />
        <Field term="What it changes" detail={card.whatItChanges} />
        <Field term="Effort" detail={card.effort} />
      </dl>

      <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[#e5e7eb]">
        {assets?.templateHref ? (
          <a
            href={assets.templateHref}
            download={assets.templateFileName ?? undefined}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#18365E] hover:underline"
          >
            <Download className="w-3.5 h-3.5" />
            Download template
          </a>
        ) : (
          <span className="text-[11px] text-[#717182]">{NO_TEMPLATE_NOTE}</span>
        )}

        {assets?.exampleHref && (
          <a
            href={assets.exampleHref}
            download={assets.exampleFileName}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#717182] hover:underline"
          >
            <Eye className="w-3.5 h-3.5" />
            View example
          </a>
        )}
      </div>
    </div>
  );
}

function Field({ term, detail }: { term: string; detail: string }) {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#717182]">{term}</dt>
      <dd className="text-xs text-[#18365E] leading-snug mt-0.5">{detail}</dd>
    </div>
  );
}

export default EAUploadPathsPanel;
